/** Traveller row with optional checkout selection sequence (not sent to API). */
export interface TravellerWithSelectionOrder {
  selectionOrder?: number;
  isSelected?: boolean;
}

/** Traveller row that may carry a persisted API id or a client-assigned negative id. */
export interface TravellerWithCheckoutId extends TravellerWithSelectionOrder {
  travellerId?: number | string;
}

export function normalizeTravellerId(
  id: number | string | undefined | null
): string | undefined {
  if (id == null || id === "") return undefined;
  return String(id);
}

/** Whether the traveller row already has a persisted checkout id (API or client-assigned). */
export function hasTravellerId(traveller: TravellerWithCheckoutId): boolean {
  return normalizeTravellerId(traveller.travellerId) !== undefined;
}

/** Next client-assigned id for travellers without an API `travellerId` (-1, -2, …). */
export function getNextNegativeTravellerId(
  travellers: TravellerWithCheckoutId[]
): number {
  const minNeg = travellers.reduce((min, traveller) => {
    const normalized = normalizeTravellerId(traveller.travellerId);
    if (!normalized) return min;
    const num = Number(normalized);
    if (!Number.isNaN(num) && num < 0) return Math.min(min, num);
    return min;
  }, 0);
  return minNeg <= 0 ? minNeg - 1 : -1;
}

/**
 * Assigns negative `travellerId` only to rows that do not already have one.
 * Existing API ids are never modified, including after the user edits traveller data.
 */
export function ensureTravellerIds<T extends TravellerWithCheckoutId>(
  travellers: T[]
): T[] {
  const result: T[] = [];
  for (const traveller of travellers) {
    if (hasTravellerId(traveller)) {
      result.push(traveller);
      continue;
    }
    result.push({
      ...traveller,
      travellerId: getNextNegativeTravellerId(result),
    });
  }
  return result;
}

function resolveCheckoutTravellerIdForTraveller(
  traveller: TravellerWithCheckoutId,
  negativeCounter: { next: number }
): string {
  const existing = normalizeTravellerId(traveller.travellerId);
  if (existing !== undefined) return existing;
  const id = negativeCounter.next;
  negativeCounter.next -= 1;
  return String(id);
}

/** Selected travellers with the `travellerId` values sent to checkout (selection order preserved). */
export function getCheckoutTravellerIdAssignments<
  T extends TravellerWithCheckoutId
>(
  travellers: T[]
): { traveller: T; formIndex: number; checkoutTravellerId: string }[] {
  const selectedWithIndex = travellers
    .map((traveller, index) => ({ traveller, index }))
    .filter(({ traveller }) => traveller.isSelected)
    .sort(
      (a, b) =>
        (a.traveller.selectionOrder ?? Number.MAX_SAFE_INTEGER) -
        (b.traveller.selectionOrder ?? Number.MAX_SAFE_INTEGER)
    );

  const negativeCounter = { next: -1 };
  return selectedWithIndex.map(({ traveller, index }) => ({
    traveller,
    formIndex: index,
    checkoutTravellerId: resolveCheckoutTravellerIdForTraveller(
      traveller,
      negativeCounter
    ),
  }));
}

/** Highest assigned `selectionOrder`, or -1 when none exist. */
export function getMaxSelectionOrder(
  travellers: TravellerWithSelectionOrder[]
): number {
  return travellers.reduce((max, traveller) => {
    const order = traveller.selectionOrder;
    return order != null && order >= 0 ? Math.max(max, order) : max;
  }, -1);
}

/** Next `selectionOrder` for a newly selected or manually added traveller. */
export function getNextSelectionOrder(
  travellers: TravellerWithSelectionOrder[]
): number {
  return getMaxSelectionOrder(travellers) + 1;
}

/**
 * Assigns sequential `selectionOrder` to pre-selected travellers on page load.
 * Restores cached values when present; returns the next counter value.
 */
export function assignSelectionOrdersToPrefilledTravellers<
  T extends TravellerWithSelectionOrder
>(travellers: T[]): { travellers: T[]; nextCounter: number } {
  let nextCounter = 0;
  const result = travellers.map((traveller) => {
    if (traveller.selectionOrder != null && traveller.selectionOrder >= 0) {
      nextCounter = Math.max(nextCounter, traveller.selectionOrder + 1);
      return traveller;
    }
    if (traveller.isSelected) {
      const withOrder = { ...traveller, selectionOrder: nextCounter };
      nextCounter += 1;
      return withOrder;
    }
    return traveller;
  });
  return { travellers: result, nextCounter };
}

/** Selected travellers first (ascending `selectionOrder`), then unselected. */
export function reorderTravellersBySelectionOrder<
  T extends TravellerWithSelectionOrder
>(travellers: T[]): T[] {
  const selected = travellers
    .filter((traveller) => traveller.isSelected)
    .sort(
      (a, b) =>
        (a.selectionOrder ?? Number.MAX_SAFE_INTEGER) -
        (b.selectionOrder ?? Number.MAX_SAFE_INTEGER)
    );
  const unselected = travellers.filter((traveller) => !traveller.isSelected);
  return [...selected, ...unselected];
}

/** Selected travellers sorted by `selectionOrder` for checkout payload. */
export function getSelectedTravellersSortedBySelectionOrder<
  T extends TravellerWithSelectionOrder
>(travellers: T[]): T[] {
  return travellers
    .filter((traveller) => traveller.isSelected)
    .sort(
      (a, b) =>
        (a.selectionOrder ?? Number.MAX_SAFE_INTEGER) -
        (b.selectionOrder ?? Number.MAX_SAFE_INTEGER)
    );
}

/**
 * Maps checkout `travellerId` from the API error payload to the current form array index.
 * Matches persisted API ids, client-assigned negative ids, and legacy numeric indices (0, 1, 2…).
 */
export function findTravellerIndexByCheckoutId<
  T extends TravellerWithCheckoutId
>(travellers: T[], checkoutTravellerId: string | number): number {
  const normalizedError = normalizeTravellerId(checkoutTravellerId);
  if (!normalizedError) return -1;

  const assignments = getCheckoutTravellerIdAssignments(travellers);
  const assignmentMatch = assignments.find(
    (entry) => entry.checkoutTravellerId === normalizedError
  );
  if (assignmentMatch) return assignmentMatch.formIndex;

  const directIndex = travellers.findIndex(
    (traveller) =>
      normalizeTravellerId(traveller.travellerId) === normalizedError
  );
  if (directIndex !== -1) return directIndex;

  const asNum = Number(normalizedError);
  if (!Number.isNaN(asNum) && Number.isInteger(asNum) && asNum >= 0) {
    const legacyMatch = assignments[asNum];
    if (legacyMatch) return legacyMatch.formIndex;
    if (asNum < travellers.length) return asNum;
  }

  return -1;
}

/** Assigns `selectionOrder` 0…n-1 to Excel-imported travellers (selection batch). */
export function assignSelectionOrdersToExcelTravellers<T>(
  travellers: T[]
): T[] {
  return travellers.map((traveller, index) => ({
    ...traveller,
    selectionOrder: index,
  }));
}
